import React from "react";
import { IconCube, IconFileCertificate, IconStack2 } from "@tabler/icons-react";
import Tooltip from "components/Tooltip";
import { getCurrentReferenceValue } from "./utils";
import { ProductSearchOutput } from "api-client";
import { useTranslation } from "react-i18next";
import { useComplianceInfo } from "hooks/useComplianceInfo";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

function ProductComponentInfo({ item }: { item: ProductSearchOutput }) {
  const { i18n, t } = useTranslation();
  const isEng = i18n.language === "en";
  const itemReferenceUnit = getCurrentReferenceValue(
    item.reference_unit.toUpperCase()
  );
  const { getCertificationChipsGroup, getConformityChipsGroup } =
    useComplianceInfo();

  function sentinelChip() {
    return (
      <Tooltip
        content={
          isFullyCertified()
            ? t("providers.sentinelTooltip")
            : t("providers.partlySentinelTooltip")
        }
      >
        <div className="my-1 flex items-center px-2 h-[18px] bg-green-100 rounded-full whitespace-nowrap">
          <IconFileCertificate
            className="w-4 h-4 text-green-600 mr-1"
            stroke={2.5}
          />
          <p className="text-xs inline font-medium text-green-700">
            {isFullyCertified()
              ? t("providers.sentinel")
              : t("providers.partlySentinel")}
          </p>
        </div>
      </Tooltip>
    );
  }

  const isFullyCertified = () => {
    const allEpds = item.groups
      ?.map((group) => group.epds)
      .flat()
      .map((epd) => epd?.epd)
      .flat();

    const sentinelCount = allEpds
      ?.map((epd) => epd?.documents)
      .flat()
      .filter((rec) => rec?.provider === "Sentinel").length;

    return allEpds?.length === sentinelCount;
  };

  if (!item) return <></>;
  const hasSentinel = (item.epd_providers ?? []).includes("Sentinel");

  return (
    <div className="flex overflow-hidden">
      {item.type === "PRODUCT" ? (
        <IconCube
          stroke={3}
          className="bg-teal-300 rounded-full p-[5px] min-w-[24px] min-h-[24px] mr-3 text-teal-900"
        />
      ) : (
        <IconStack2
          stroke={3}
          className="bg-cyan-300 rounded-full p-[5px] min-w-[26px] min-h-[26px] mr-3 text-cyan-900"
        />
      )}
      <div className="w-full">
        <div className="flex items-center flex-wrap">
          {item.reference_unit && (
            <div className="bg-gray-200 text-xs rounded-full py-1 px-2 mr-1 flex items-center">
              {isEng ? itemReferenceUnit?.name : itemReferenceUnit?.name_de}
            </div>
          )}
          <div
            className={clsx(
              "flex items-center border-l border-gray-200 pl-1 h-6",
              { "border-r mr-1": hasSentinel }
            )}
          >
            <div className="flex border-r border-gray-200 mr-1 h-full items-center">
              {getConformityChipsGroup(item?.compliant_conformities_names)}
            </div>
            {getCertificationChipsGroup(item?.compliant_certifications_names)}
          </div>
          {hasSentinel && sentinelChip()}
        </div>
        <div className="text-base font-semibold text-gray-800 mt-[6px]">
          {item.name}
        </div>
        <div className="flex mt-1">
          {item.din_category_number && (
            <span className="text-gray-500 text-sm">
              {item.din_category_number}
            </span>
          )}
          {item.din_category && (
            <span className="text-gray-500 text-sm pl-1 overflow-hidden">
              {item.din_category}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default observer(ProductComponentInfo);
